import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export const EMPTY_CUSTOMER_FILTERS = {
  packageName: "",
  projectAddress: "",
  propertyAddress: "",
  propertyOwnerName: "",
  county: "",
  city: "",
  contractorName: "",
  contractorLicenseNumber: "",
  contractorLicenseType: "",
  nocCreated: "",
  permitPackageCreated: "",
  notarized: "",
  recorded: "",
  connectedToCity: "",
  initiatedFrom: "",
  initiatedUntil: "",
};

const PROPERTY_FIELDS = [
  { key: "packageName", label: "Package Name", optionsKey: "packageNames" },
  {
    key: "projectAddress",
    label: "Project Address",
    optionsKey: "projectAddresses",
  },
  {
    key: "propertyAddress",
    label: "Property Address",
    optionsKey: "propertyAddresses",
  },
  {
    key: "propertyOwnerName",
    label: "Property Owner Name",
    optionsKey: "propertyOwnerNames",
  },
  { key: "county", label: "County", optionsKey: "counties" },
  { key: "city", label: "City", optionsKey: "cities" },
];

const CONTRACTOR_FIELDS = [
  {
    key: "contractorName",
    label: "Contractor Business Name",
    optionsKey: "contractorNames",
  },
  {
    key: "contractorLicenseNumber",
    label: "Contractor License Number",
    optionsKey: "contractorLicenseNumbers",
  },
  {
    key: "contractorLicenseType",
    label: "Contractor License Type",
    optionsKey: "contractorLicenseTypes",
  },
];

const YES_NO_FIELDS = [
  { key: "nocCreated", label: "NOC Created" },
  { key: "permitPackageCreated", label: "Permit Package Created" },
  { key: "notarized", label: "Notarized" },
  { key: "recorded", label: "Recorded" },
];

const formatChipLabel = (key, value) => {
  const name = key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase());
  if (key === "initiatedFrom" || key === "initiatedUntil") {
    return `${name}: ${new Date(value).toLocaleDateString()}`;
  }
  return `${name}: ${value}`;
};

const YesNoAnyField = ({ label, value, onChange }) => (
  <Box>
    <Typography
      variant="body2"
      sx={{ mb: 0.75, fontWeight: 500, color: "text.secondary" }}
    >
      {label}
    </Typography>
    <ToggleButtonGroup
      exclusive
      fullWidth
      size="small"
      color="primary"
      value={value}
      aria-label={label}
      onChange={(_, v) => {
        // exclusive group sends null when the active button is clicked again
        if (v !== null) onChange(v);
      }}
      sx={{
        bgcolor: "background.paper",
        "& .MuiToggleButton-root": {
          textTransform: "none",
          py: 0.5,
          fontWeight: 500,
        },
      }}
    >
      <ToggleButton value="yes">Yes</ToggleButton>
      <ToggleButton value="no">No</ToggleButton>
      <ToggleButton value="">Any</ToggleButton>
    </ToggleButtonGroup>
  </Box>
);

const Section = ({ icon, title, children }) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      p: { xs: 2, sm: 2.5 },
      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "primary.main",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          "& svg": { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, color: "primary.main" }}
      >
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

const fieldGridSx = (minWidth = 220) => ({
  display: "grid",
  gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))`,
  gap: 2,
});

const autocompleteInputSx = {
  "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
};

/**
 * Props:
 *  open, onClose
 *  appliedFilters      -> object | null (what the grid is currently filtered by)
 *  onApply(filters)    -> called with object | null
 *  autocompleteOptions -> optional { packageNames: [], projectAddresses: [], ... }
 */
const CustomersPackagesFilterModal = ({
  open,
  onClose,
  appliedFilters,
  onApply,
  autocompleteOptions = {},
}) => {
  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [draft, setDraft] = useState(EMPTY_CUSTOMER_FILTERS);

  // Re-sync the draft with what is applied every time the modal opens
  useEffect(() => {
    if (open) {
      setDraft({ ...EMPTY_CUSTOMER_FILTERS, ...(appliedFilters || {}) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setField = (key, value) =>
    setDraft((prev) => ({ ...prev, [key]: value ?? "" }));

  const handleApply = () => {
    const cleaned = Object.entries(draft).reduce((acc, [key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});
    onApply(Object.keys(cleaned).length > 0 ? cleaned : null);
    onClose();
  };

  const handleClearAll = () => {
    setDraft(EMPTY_CUSTOMER_FILTERS);
    onApply(null);
  };

  const handleRemoveChip = (key) => {
    const next = { ...(appliedFilters || {}) };
    delete next[key];
    setField(key, "");
    onApply(Object.keys(next).length > 0 ? next : null);
  };

  const renderAutocomplete = ({ key, label, optionsKey }) => (
    <Autocomplete
      key={key}
      freeSolo
      size="small"
      options={autocompleteOptions?.[optionsKey] || []}
      inputValue={draft[key]}
      onInputChange={(_, newInputValue) => setField(key, newInputValue)}
      renderInput={(params) => (
        <TextField {...params} label={label} sx={autocompleteInputSx} />
      )}
    />
  );

  const activeEntries = Object.entries(appliedFilters || {});

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={mobileSize}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: mobileSize ? 0 : 3,
            maxHeight: mobileSize ? "100vh" : "90vh",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          py: 2,
          px: { xs: 2, sm: 3 },
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Search & Filter Customer Packages
          </Typography>
          {activeEntries.length > 0 && (
            <Chip
              label={`${activeEntries.length} active`}
              size="small"
              color="primary"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close"
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ px: { xs: 2, sm: 3 }, pt: "20px !important", pb: 3 }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Section icon={<HomeWorkOutlinedIcon />} title="Property Details">
            <Box sx={fieldGridSx(240)}>
              {PROPERTY_FIELDS.map(renderAutocomplete)}
            </Box>
          </Section>

          <Section
            icon={<EngineeringOutlinedIcon />}
            title="Contractor Information"
          >
            <Box sx={fieldGridSx(240)}>
              {CONTRACTOR_FIELDS.map(renderAutocomplete)}
            </Box>
          </Section>

          <Section icon={<FactCheckOutlinedIcon />} title="Status Filters">
            <Box sx={fieldGridSx(200)}>
              {YES_NO_FIELDS.map(({ key, label }) => (
                <YesNoAnyField
                  key={key}
                  label={label}
                  value={draft[key]}
                  onChange={(v) => setField(key, v)}
                />
              ))}

              <YesNoAnyField
                label="Connected to City"
                value={draft.connectedToCity}
                onChange={(v) => setField("connectedToCity", v)}
              />
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <Typography
                variant="body2"
                sx={{ mb: 0.75, fontWeight: 500, color: "text.secondary" }}
              >
                Initiated Date Range
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "minmax(160px, 220px) auto minmax(160px, 220px)",
                  },
                  gap: 1.5,
                  alignItems: "center",
                }}
              >
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="en"
                >
                  <DatePicker
                    label="From"
                    format="MM/DD/YYYY"
                    value={
                      draft.initiatedFrom
                        ? dayjs(draft.initiatedFrom).utc()
                        : null
                    }
                    onChange={(v) =>
                      setField(
                        "initiatedFrom",
                        v
                          ? v
                              .startOf("day")
                              .utc()
                              .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
                          : "",
                      )
                    }
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: autocompleteInputSx,
                      },
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      textAlign: "center",
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    to
                  </Typography>
                  <DatePicker
                    label="Until"
                    format="MM/DD/YYYY"
                    value={
                      draft.initiatedUntil
                        ? dayjs(draft.initiatedUntil).utc()
                        : null
                    }
                    onChange={(v) =>
                      setField(
                        "initiatedUntil",
                        v
                          ? v
                              .endOf("day")
                              .utc()
                              .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
                          : "",
                      )
                    }
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: autocompleteInputSx,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          </Section>
        </Box>
      </DialogContent>

      {activeEntries.length > 0 && (
        <Box
          sx={{
            py: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 0.75,
              fontWeight: 600,
              color: "text.secondary",
              px: { xs: 2, sm: 3 },
            }}
          >
            Active Filters
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignContent: "flex-start",
              gap: 1,
              maxHeight: 80,
              overflowY: "auto",
              px: { xs: 2, sm: 3 },
              py: 0.5,
              boxSizing: "border-box",
            }}
          >
            {activeEntries.map(([key, value]) => (
              <Chip
                key={key}
                label={formatChipLabel(key, value)}
                onDelete={() => handleRemoveChip(key)}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ bgcolor: "background.paper" }}
              />
            ))}
          </Box>
        </Box>
      )}

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "space-between",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          onClick={handleClearAll}
          color="secondary"
          variant="outlined"
          startIcon={<RestartAltIcon />}
        >
          Clear All
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            startIcon={<FilterListIcon />}
          >
            Apply Filters
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(CustomersPackagesFilterModal);
